import type { ReactNode } from "react";

/**
 * Iconografía propia de Argus — trazo fino consistente (1.6), esquinas
 * redondeadas, sin dependencias. Reemplaza los emojis del rail (issue #50).
 * El ojo de Argos es el icono de Pendientes: la sección de vigilancia.
 * Principio de marca: iconos de visión/observación; nunca escudos ni candados.
 */

const TAMANO_ICONO = 18;

type IconoProps = { size?: number };

function Svg({ size = TAMANO_ICONO, children }: IconoProps & { children: ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

export function IconoInicio(p: IconoProps) {
  return (
    <Svg {...p}>
      <path d="M4 11 L12 4 L20 11" />
      <path d="M6 10 V19 H18 V10" />
    </Svg>
  );
}

/** El ojo de Argos — isotipo de la marca como icono de vigilancia. */
export function IconoOjo(p: IconoProps) {
  return (
    <Svg {...p}>
      <path d="M2.5 12 Q12 4.5 21.5 12 Q12 19.5 2.5 12 Z" />
      <circle cx="12" cy="12" r="3.2" />
    </Svg>
  );
}

export function IconoCaptacion(p: IconoProps) {
  return (
    <Svg {...p}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5 V16.5 M9.5 15 Q12 16.8 14.2 15.2 Q15 13.4 12 12 Q9 10.6 9.9 8.8 Q12 7.2 14.5 9" />
    </Svg>
  );
}

export function IconoEdificio(p: IconoProps) {
  return (
    <Svg {...p}>
      <path d="M5 20 V6 L13 3.5 V20" />
      <path d="M13 9 L19 11 V20" />
      <path d="M3 20 H21" />
      <path d="M8 8.5 H10 M8 12 H10 M8 15.5 H10" />
    </Svg>
  );
}

export function IconoLlave(p: IconoProps) {
  return (
    <Svg {...p}>
      <circle cx="8" cy="8" r="4.5" />
      <path d="M11.5 11.5 L20 20 M17 17 L19.5 14.5 M14.5 14.5 L16.5 12.5" />
    </Svg>
  );
}

export function IconoPersonas(p: IconoProps) {
  return (
    <Svg {...p}>
      <circle cx="9" cy="8.5" r="3.4" />
      <path d="M3.5 19 Q5 14.5 9 14.5 Q13 14.5 14.5 19" />
      <circle cx="16.5" cy="9.5" r="2.7" />
      <path d="M15.5 14 Q19.5 14 20.5 18" />
    </Svg>
  );
}

export function IconoProyecto(p: IconoProps) {
  return (
    <Svg {...p}>
      <path d="M4 20 L4 9 L12 4 L20 9 L20 20" />
      <path d="M9 20 V13 H15 V20" />
      <path d="M2.5 20 H21.5" />
    </Svg>
  );
}

export function IconoReporte(p: IconoProps) {
  return (
    <Svg {...p}>
      <path d="M6 20 H18 V7.5 L14.5 4 H6 Z" />
      <path d="M14 4 V8 H18" />
      <path d="M12 16.5 V10.5 M9.5 13 L12 10.5 L14.5 13" />
    </Svg>
  );
}

export function IconoCumplimiento(p: IconoProps) {
  return (
    <Svg {...p}>
      <path d="M7 4 H17 V20 H7 Z" />
      <path d="M10 8 L11.2 9.2 L14 6.5 M10 13 L11.2 14.2 L14 11.5" />
      <path d="M10 17.5 H14" />
    </Svg>
  );
}

export function IconoReloj(p: IconoProps) {
  return (
    <Svg {...p}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5 V12 L15.5 14" />
    </Svg>
  );
}

export function IconoEngrane(p: IconoProps) {
  return (
    <Svg {...p}>
      <circle cx="12" cy="12" r="3.2" />
      <path d="M12 3.5 V6 M12 18 V20.5 M3.5 12 H6 M18 12 H20.5 M6 6 L7.8 7.8 M16.2 16.2 L18 18 M18 6 L16.2 7.8 M7.8 16.2 L6 18" />
    </Svg>
  );
}

/**
 * Isotipo de Argus (ojo de Argos) — recreación vectorial del logo de
 * `marca/visual/referencias/`. `colorPupila` permite la variante del rail
 * (pupila oro sobre tiel fuerte).
 */
const ALTO_LOGO = 26;
const VIEWBOX_ANCHO = 120;
const VIEWBOX_ALTO = 74;

export function LogoArgus({
  alto = ALTO_LOGO,
  color = "#0F5257",
  colorPupila,
}: {
  alto?: number;
  color?: string;
  colorPupila?: string;
}) {
  const ancho = Math.round(alto * (VIEWBOX_ANCHO / VIEWBOX_ALTO));
  return (
    <svg width={ancho} height={alto} viewBox="0 0 120 74" fill="none" aria-label="Argus">
      <path d="M 8 37 Q 60 4 112 37 Q 60 70 8 37 Z" stroke={color} strokeWidth={4} fill="none" />
      <circle cx="60" cy="37" r="25" stroke={color} strokeWidth={4} fill="none" />
      <circle cx="60" cy="37" r="9" fill={colorPupila ?? color} />
    </svg>
  );
}
